import type { ActionKey, ContextItem, DocItem, Flag } from "./types";

// Suggested docs revealed once project type is determined as a deal analysis.
export const DEAL_SUGGESTED_DOCS: DocItem[] = [
    {
        id: "orderForm",
        label: "Order Form",
        required: true,
        state: "suggested",
    },
    {
        id: "msa",
        label: "MSA / AWS Customer Agreement",
        required: true,
        state: "suggested",
    },
];

export const INITIAL_DOCUMENTS: DocItem[] = [];

// Deal-specific context topics, pre-populated since the project type is known
// upfront in this demo (entry point was "Analyze contracts").
export const DEAL_CONTEXT_ITEMS: ContextItem[] = [
    {
        id: "commercial",
        label: "Commercial objectives",
        hint: "What you want, what you’ll trade, how hard you’ll push",
        status: "pending",
    },
    {
        id: "risks",
        label: "Risks & concerns",
        hint: "Contract risks and specific things to scrutinize",
        status: "pending",
    },
    {
        id: "relationship",
        label: "Vendor relationship",
        hint: "Performance, history, past negotiations",
        status: "pending",
    },
    {
        id: "alternatives",
        label: "Market alternatives",
        hint: "BATNA and switching cost",
        status: "pending",
    },
];

// Empty until project type is discovered (via talkProjectType or by uploading
// a document the system can identify).
export const INITIAL_CONTEXT: ContextItem[] = [];

export const INITIAL_AVAILABLE_ACTIONS: ActionKey[] = ["uploadOrderForm", "talkProjectType"];

export const HEADER_FROM_ORDER_FORM = {
    dealName: "AWS Private Pricing Addendum",
    vendor: "Amazon Web Services",
    tcv: "$173M",
    isRenewal: false,
    startDate: "Jan 2026",
    endDate: "Dec 2028",
};

export const HEADER_SUMMARY =
    "Three-year AWS Private Pricing Addendum committing Demo Customer to $173M of cloud spend. " +
    "Compute Savings Plan depth, the back-loaded ramp curve, and the Marketplace cap are the key economic levers. " +
    "Azure and GCP parallel proposals provide credible BATNA for Round 2.";

export const DPA_SUGGESTED_DOC: DocItem = {
    id: "dpa",
    label: "Data Processing Addendum",
    required: false,
    state: "suggested",
    hint: "Referenced in AWS Customer Agreement §11 but not attached.",
};

export const MSA_FLAGS: Flag[] = [
    {
        id: "flag-ramp",
        title: "Aggressive YoY ramp curve",
        detail: "Addendum escalates 28% / 32% / 40%, well above the 15% / 20% / 25% market band.",
    },
    {
        id: "flag-termination",
        title: "Termination for convenience stripped",
        detail: "Only termination-for-cause survives PPA execution; prior agreements retained ToC rights.",
    },
    {
        id: "flag-egress",
        title: "Egress pricing not stepped down",
        detail: "Standard tiered egress applies; no negotiated step-down in §3.4.",
    },
];

export const ORDER_FORM_DOC = {
    id: "orderForm" as const,
    filename: "AWS - Private Pricing Addendum (Order Form).pdf",
    size: "186 KB",
    detectedType: "Order form",
    uploadMs: 1200,
    parseMs: 2400,
};

export const MSA_DOC = {
    id: "msa" as const,
    filename: "AWS Customer Agreement (executed) - Demo Customer.pdf",
    size: "412 KB",
    detectedType: "MSA",
    uploadMs: 1400,
    parseMs: 3600,
};

export const TRANSCRIPTS: Record<
    | "talkProjectType"
    | "talkCommercial"
    | "talkRisks"
    | "talkRelationship"
    | "talkAlternatives",
    string
> = {
    talkProjectType:
        "This is a deal analysis. We're renewing our enterprise commitment with AWS. " +
        "The agreement is a Private Pricing Addendum, three-year term.",
    talkCommercial:
        "So this is our first formal Private Pricing Addendum with AWS. We've been on consumption pricing up to now. " +
        "The big commercial goals are getting Compute Savings Plan depth closer to market (they're offering 22%, we should be in the 30s at our commitment size) and lifting the Marketplace spend cap from 25% to something like 50%. " +
        "Posture is firm but not aggressive. We want to close this, but we have specific levers we won't drop. " +
        "We're willing to trade a longer term and stronger upfront commitment for ramp flexibility and better SP depth.",
    talkRisks:
        "On the paper side, my top concerns are termination rights and the ramp curve in the addendum. " +
        "The PPA strips termination-for-convenience, which we've had in every prior framework agreement, and the YoY ramp is back-loaded in a way that doesn't reflect our actual demand curve. " +
        "Egress pricing is also a real exposure. There's no negotiated step-down so if we ever need to move workloads we get penalized. " +
        "Standard AWS Customer Agreement IP and liability terms are fine; we don't need to fight those.",
    talkRelationship:
        "Account team has been strong. Our AE Priya is consultative, not just transactional, and the solutions architects have been embedded with our platform group for the last 18 months. " +
        "Where AWS gets rigid is the PPA team itself. They're discretionary on the levers they'll move and they slow-walk anything that touches headline price. " +
        "We've gotten meaningful credit conversions in past negotiations but nothing structural on commit shape. " +
        "Technical relationship is genuinely good; commercial relationship is where the friction sits.",
    talkAlternatives:
        "We're running parallel proposals from Azure and GCP, not because we actually want to migrate but because we need credible BATNA going into Round 2. " +
        "Switching is realistic for new workloads, much harder for the existing platform footprint given how deep the AWS-native dependencies run. " +
        "Azure is the stronger commercial alternative; GCP is more credible on the AI/ML side specifically. " +
        "The analyst should treat the alternatives as leverage, not as a real path.",
};

export const CHIP_LABELS: Record<ActionKey, string> = {
    uploadOrderForm: "📎 Upload the Order Form",
    uploadMSA: "📎 Upload the AWS Customer Agreement",
    talkProjectType: "🎤 Tell me what kind of project this is",
    talkCommercial: "🎤 Talk about commercial objectives",
    talkRisks: "🎤 Talk about risks & concerns",
    talkRelationship: "🎤 Talk about the AWS relationship",
    talkAlternatives: "🎤 Talk about market alternatives",
    review: "Review & submit →",
};

// Order in which a "talk*" chip becomes available after another one finishes.
export const NEXT_TALK_ACTION: Record<
    "talkCommercial" | "talkRisks" | "talkRelationship" | "talkAlternatives",
    ActionKey | null
> = {
    talkCommercial: "talkRisks",
    talkRisks: "talkRelationship",
    talkRelationship: "talkAlternatives",
    talkAlternatives: null,
};

export const CONTEXT_FROM_ACTION: Record<
    "talkCommercial" | "talkRisks" | "talkRelationship" | "talkAlternatives",
    "commercial" | "risks" | "relationship" | "alternatives"
> = {
    talkCommercial: "commercial",
    talkRisks: "risks",
    talkRelationship: "relationship",
    talkAlternatives: "alternatives",
};

export const BOT_GREETING =
    "Hi, I'll help you submit this project for analyst review. " +
    "To start, what kind of project is this: a Deal analysis (renewal, repricing, contract review) or an RFP/RFI/RFQ (vendor selection)? " +
    "You can also just upload documents and I'll figure it out from there.";

export const BOT_AFTER_PROJECT_TYPE_DEAL =
    "Got it, this is a deal analysis. I've set the name for your project and populated suggested documents and topics to cover. " +
    "Tell me about your commercial objectives next: what you want, what you'll trade, how hard you want to push.";

export const BOT_ON_ORDER_FORM_UPLOADED = "Got it. Reading the document now.";

export const BOT_ON_MSA_UPLOADED = "Reading the document now.";

export const BOT_AFTER_ORDER_FORM_READY = {
    intro: "Done reading the order form.",
    body:
        "AWS Private Pricing Addendum, $173M committed spend over 36 months, term Jan 2026 – Dec 2028. " +
        "Compute Savings Plans at 22% depth, Marketplace cap at 25%. This is a deal analysis. I've set the name for your project and suggested documents and topics to cover.",
    followUp: "I still need the MSA / AWS Customer Agreement.",
};

export const BOT_AFTER_MSA_READY = {
    intro: "Done reading the AWS Customer Agreement.",
    body:
        "Three-year master agreement with PPA-specific terms layered on top. A few items I'd flag for the analyst: the YoY ramp escalates 28% / 32% / 40%, well above the 15% / 20% / 25% market band; termination-for-convenience is stripped; egress pricing defaults to standard tiers with no negotiated step-down in §3.4.",
    followUp: "Heads up: the Data Processing Addendum is referenced in §11 but not attached.",
};

export const BOT_REPLIES_AFTER_CONTEXT: Record<
    "commercial" | "risks" | "relationship" | "alternatives",
    string
> = {
    commercial:
        "Got it. Savings Plan depth and the Marketplace cap as the top economic levers, posture is firm, and you're willing to trade term length for ramp flexibility. " +
        "Next, what are the contractual risks you want me to scrutinize?",
    risks:
        "Noted. Termination, ramp shape, and egress are the contractual exposures; IP and liability stay as-is. " +
        "How would you describe the vendor relationship: performance, history, past negotiations?",
    relationship:
        "Got the relationship picture: technical side strong, PPA-team rigid on structural commit shape. " +
        "Last topic: what are your market alternatives? What's your BATNA going into Round 2?",
    alternatives:
        "Got the BATNA picture: Azure and GCP as leverage, not a real migration path. " +
        "Combined with the other context the analyst has plenty to work with. Hit “Review & submit” when you're ready.",
};

export const REF_ID = "DEAL-2026-0418";
